// Jun-2026 — OEM Complaint module.
// Raise client-site complaints to the responsible Vendor (OEM) and chase
// them by email (immediate + 10:00/18:00 IST) until Resolved.
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Plus, Upload, RotateCw, Search, Mail, CheckCircle2, Tag, Eye,
  Send, AlertTriangle, X, BarChart3, ListChecks, Undo2, Ban, Download, Trash2,
  Pause, Play, MessageSquarePlus,
} from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { PaginationControls } from "@/components/PaginationControls";
import { useAuth } from "@/contexts/AuthContext";
import { useRef } from "react";
import { toPng } from "html-to-image";

const inputCls = "w-full px-2.5 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })
      + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    "Open":         "bg-slate-200 text-slate-700",
    "Mail Sent":    "bg-sky-100 text-sky-800",
    "Client Scope": "bg-violet-100 text-violet-800",
    "Resolved":     "bg-emerald-100 text-emerald-800",
  };
  const label = status === "Mail Sent" ? "Register Mail Sent" : status;
  return (
    <span data-testid={`oem-status-${(status || "").toLowerCase().replace(/\s/g, "-")}`}
      className={`inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 whitespace-nowrap ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {label || "—"}
    </span>
  );
}

function PausedBadge({ remark }) {
  return (
    <span data-testid="oem-mails-paused-badge"
      title={remark ? `Auto mails paused — ${remark}` : "Auto mails paused"}
      className="inline-flex items-center gap-0.5 ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 whitespace-nowrap">
      <Pause size={8} /> Mails Paused
    </span>
  );
}

function RowActions({ c, onView, onTag, onSendNow, onClientScope, onResolve, onReopen, onDelete, canDelete, onPause, onResume, onCommunicate, size = 14, pad = "p-1", idPrefix = "oem" }) {
  return (
    <span className="whitespace-nowrap">
      <button data-testid={`${idPrefix}-view-${c.ticket_number}`} onClick={() => onView(c)}
        className={`text-sky-600 hover:text-sky-800 ${pad}`} title="View details & mail history"><Eye size={size} /></button>
      <button data-testid={`${idPrefix}-comm-${c.ticket_number}`} onClick={() => onCommunicate(c)}
        className={`text-teal-600 hover:text-teal-800 ${pad}`} title="Add communication / update note"><MessageSquarePlus size={size} /></button>
      {c.status !== "Resolved" && c.status !== "Client Scope" && (
        <>
          <button data-testid={`${idPrefix}-tag-${c.ticket_number}`} onClick={() => onTag(c)}
            className={`text-indigo-500 hover:text-indigo-700 ${pad}`} title="Tag Vendor Complaint Register Number"><Tag size={size} /></button>
          <button data-testid={`${idPrefix}-sendnow-${c.ticket_number}`} onClick={() => onSendNow(c)}
            className={`text-[#F97316] hover:text-[#EA580C] ${pad}`} title="Send mail now"><Send size={size} /></button>
          {!c.mails_paused ? (
            <button data-testid={`${idPrefix}-pause-${c.ticket_number}`} onClick={() => onPause(c)}
              className={`text-amber-500 hover:text-amber-700 ${pad}`} title="Stop auto mails for this complaint"><Pause size={size} /></button>
          ) : (
            <button data-testid={`${idPrefix}-resume-${c.ticket_number}`} onClick={() => onResume(c)}
              className={`text-emerald-500 hover:text-emerald-700 ${pad}`} title="Restart auto mails — fresh restart, no backlog mails"><Play size={size} /></button>
          )}
          <button data-testid={`${idPrefix}-clientscope-${c.ticket_number}`} onClick={() => onClientScope(c)}
            className={`text-violet-500 hover:text-violet-700 ${pad}`} title="Mark Client Scope (stops mails)"><Ban size={size} /></button>
          <button data-testid={`${idPrefix}-resolve-${c.ticket_number}`} onClick={() => onResolve(c)}
            className={`text-emerald-600 hover:text-emerald-800 ${pad}`} title="Mark Resolved (stops mails)"><CheckCircle2 size={size} /></button>
          {canDelete && (
            <button data-testid={`${idPrefix}-delete-${c.ticket_number}`} onClick={() => onDelete(c)}
              className={`text-rose-500 hover:text-rose-700 ${pad}`} title="Delete complaint permanently"><Trash2 size={size} /></button>
          )}
        </>
      )}
      {(c.status === "Resolved" || c.status === "Client Scope") && (
        <button data-testid={`${idPrefix}-reopen-${c.ticket_number}`} onClick={() => onReopen(c)}
          className={`text-amber-500 hover:text-amber-700 ${pad}`}
          title={c.status === "Client Scope" ? "Move back to Open (remark required — mails resume)" : "Reopen (mails resume)"}><Undo2 size={size} /></button>
      )}
    </span>
  );
}

export default function OemComplaintsPage() {
  const { hasPerm } = useAuth();
  const canDelete = hasPerm("oem_complaints", "delete");
  const [tab, setTab] = useState("list");            // list | figures
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterVcn, setFilterVcn] = useState("");
  const [filterVendors, setFilterVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [tagRow, setTagRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [csRow, setCsRow] = useState(null);
  const [reopenRow, setReopenRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [pauseRow, setPauseRow] = useState(null);
  const [commRow, setCommRow] = useState(null);
  const [stats, setStats] = useState(null);

  async function load(p = page, ps = pageSize) {
    setLoading(true);
    try {
      const params = { skip: (p - 1) * ps, limit: ps };
      if (filterStatus) params.status = filterStatus;
      if (filterVendor) params.vendor_id = filterVendor;
      if (filterVcn) params.vcn = filterVcn;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/oem-complaints", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load complaints");
    } finally { setLoading(false); }
  }

  async function loadStats() {
    try {
      const { data } = await api.get("/oem-complaints/stats");
      setStats(data);
    } catch { /* stats are non-blocking */ }
    // Vendor filter options = vendors present in the register only
    api.get("/oem-complaints/filter-vendors").then(r => setFilterVendors(r.data || [])).catch(() => {});
  }

  useEffect(() => { setPage(1); load(1, pageSize); }, [filterStatus, filterVendor, filterVcn]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, pageSize); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line
  useEffect(() => { load(page, pageSize); }, [page, pageSize]); // eslint-disable-line
  useEffect(() => { loadStats(); }, [tab]); // eslint-disable-line
  useEffect(() => {
    api.get("/vendors").then(r => setVendors(r.data || [])).catch(() => {});
    api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
    api.get("/members/_pickable").then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  async function resolve(row) {
    if (!window.confirm(`Mark ticket ${row.ticket_number} as Resolved? Chase mails will stop.`)) return;
    try {
      await api.post(`/oem-complaints/${row.id}/resolve`);
      toast.success(`${row.ticket_number} marked Resolved — mails stopped`);
      load(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Resolve failed"); }
  }

  async function reopen(row) {
    if (row.status === "Client Scope") { setReopenRow(row); return; }
    if (!window.confirm(`Reopen ticket ${row.ticket_number}? Chase mails will resume.`)) return;
    try {
      await api.post(`/oem-complaints/${row.id}/reopen`);
      toast.success(`${row.ticket_number} reopened`);
      load(); loadStats();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reopen failed"); }
  }

  async function resumeMails(row) {
    if (!window.confirm(`Restart auto mails for ${row.ticket_number}? Fresh restart — no backlog mails; the next mail goes out at the next configured daily slot.`)) return;
    try {
      await api.post(`/oem-complaints/${row.id}/resume-mails`);
      toast.success(`${row.ticket_number} — auto mails restarted (next configured slot, no backlog)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Restart failed"); }
  }

  async function sendNow(row) {
    if (!window.confirm(`Send the vendor mail for ticket ${row.ticket_number} NOW?\n\nVendor: ${row.vendor_name}\nThis sends immediately, in addition to the scheduled daily mails.`)) return;
    try {
      const { data } = await api.post(`/oem-complaints/${row.id}/send-now`);
      if (data.status === "sent") toast.success(`Mail sent to ${(data.to || []).join(", ")}`);
      else toast.warning(`Mail ${data.status}: ${data.failure_reason || ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed"); }
  }

  return (
    <div className="space-y-4" data-testid="oem-complaints-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800">OEM Complaint</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Vendor chase mails fire immediately on entry, then at the daily times set in Email Configuration until Resolved.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="oem-tab-list" onClick={() => setTab("list")}
            className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold border flex items-center gap-1.5 ${tab === "list" ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <ListChecks size={12} /> Complaints
          </button>
          <button data-testid="oem-tab-figures" onClick={() => setTab("figures")}
            className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold border flex items-center gap-1.5 ${tab === "figures" ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <BarChart3 size={12} /> Figures
          </button>
          <button data-testid="oem-tab-clientscope" onClick={() => setTab("clientscope")}
            className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold border flex items-center gap-1.5 ${tab === "clientscope" ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <Ban size={12} /> Client Scope
          </button>
          <button data-testid="oem-bulk-upload-btn" onClick={() => setShowUpload(true)}
            className="border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <Upload size={12} /> Bulk Upload
          </button>
          <button data-testid="oem-bulk-update-btn" onClick={() => setShowBulkUpdate(true)}
            className="border border-sky-600 text-sky-700 hover:bg-sky-600 hover:text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <Tag size={12} /> Bulk Update
          </button>
          <button data-testid="oem-add-btn" onClick={() => setShowAdd(true)}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <Plus size={12} /> Add Complaint
          </button>
        </div>
      </div>

      {tab === "figures" ? (
        <FiguresView stats={stats} onRefresh={loadStats} />
      ) : tab === "clientscope" ? (
        <ClientScopeView stats={stats} onRefresh={loadStats} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="oem-search" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search across ALL pages — ticket, client, store, product, issue, vendor…"
                className={`${inputCls} pl-8`} />
            </div>
            <select data-testid="oem-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-2.5 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white">
              <option value="">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Mail Sent">Register Mail Sent</option>
              <option value="Client Scope">Client Scope</option>
              <option value="Resolved">Resolved</option>
            </select>
            <SearchableSelect
              value={filterVendor}
              onChange={(id) => setFilterVendor(id || "")}
              items={filterVendors}
              getKey={(v) => v.id}
              getLabel={(v) => `${v.vendor_code ? `${v.vendor_code} · ` : ""}${v.name}`}
              getSubLabel={(v) => `${v.count} complaint(s)`}
              placeholder="All Vendors"
              searchPlaceholder="Search vendor…"
              className="w-full sm:w-[220px]"
              inputClassName="py-2"
              testid="oem-filter-vendor"
            />
            <select data-testid="oem-filter-vcn" value={filterVcn} onChange={e => setFilterVcn(e.target.value)}
              className="px-2.5 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white">
              <option value="">Vendor Complaint No — All</option>
              <option value="received">Received</option>
              <option value="not_received">Not Received</option>
            </select>
            <button data-testid="oem-refresh" onClick={() => { load(); loadStats(); }}
              className="border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
              <RotateCw size={12} /> Refresh
            </button>
            <span className="text-[11px] text-slate-500 font-bold" data-testid="oem-count">{total} complaint(s)</span>
          </div>

          <div className="hidden md:block bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full" data-testid="oem-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-2 font-bold">Ticket No</th>
                  <th className="py-2 px-2 font-bold">Client</th>
                  <th className="py-2 px-2 font-bold">Store</th>
                  <th className="py-2 px-2 font-bold">Product Id</th>
                  <th className="py-2 px-2 font-bold">Issue</th>
                  <th className="py-2 px-2 font-bold">Vendor</th>
                  <th className="py-2 px-2 font-bold">Created At</th>
                  <th className="py-2 px-2 font-bold">Status</th>
                  <th className="py-2 px-2 font-bold text-center">Mails Sent</th>
                  <th className="py-2 px-2 font-bold">Vendor Complaint No</th>
                  <th className="py-2 px-2 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={11} className="py-8 text-center text-xs text-slate-400">Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={11} className="py-8 text-center text-xs text-slate-400" data-testid="oem-empty">
                    No complaints yet — add one manually or bulk upload the Excel.
                  </td></tr>
                )}
                {!loading && items.map(c => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-orange-50/30" data-testid={`oem-row-${c.ticket_number}`}>
                    <td className="py-2 px-2 text-[11px] font-mono font-bold text-slate-800 whitespace-nowrap">{c.ticket_number}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 whitespace-nowrap" title={c.client_name}>{c.client_code}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 whitespace-nowrap" title={c.store_label}>{c.store_id}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-600 font-mono">{c.product_id || "—"}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 max-w-[220px] truncate" title={`${c.issue_type} — ${c.issue_title}`}>
                      <span className="font-semibold">{c.issue_type || "—"}</span>
                      {c.issue_title ? <span className="text-slate-400"> · {c.issue_title}</span> : null}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 whitespace-nowrap" title={c.vendor_code}>{c.vendor_name}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-600 whitespace-nowrap">{fmtDT(c.created_at_source)}</td>
                    <td className="py-2 px-2">
                      <StatusBadge status={c.status} />
                      {c.mails_paused && c.status !== "Resolved" && c.status !== "Client Scope" && <PausedBadge remark={c.mails_paused_remark} />}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-700" data-testid={`oem-mails-${c.ticket_number}`}>
                        <Mail size={11} className="text-slate-400" />{c.mails_sent || 0}
                      </span>
                      {c.last_mail_status === "failed" && (
                        <span title={c.last_mail_error || "Last mail failed"}><AlertTriangle size={11} className="inline ml-1 text-rose-500" /></span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-[11px] font-mono text-slate-700 whitespace-nowrap">
                      {c.vendor_complaint_number
                        ? (<>
                            {c.vendor_complaint_number}
                            {c.vendor_complaint_date && (
                              <span className="block text-[9px] text-slate-400 font-sans">
                                {new Date(c.vendor_complaint_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                              </span>
                            )}
                          </>)
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      <RowActions c={c} onView={setViewRow} onTag={setTagRow} onSendNow={sendNow}
                        onClientScope={setCsRow} onResolve={resolve} onReopen={reopen}
                        onDelete={setDeleteRow} canDelete={canDelete}
                        onPause={setPauseRow} onResume={resumeMails} onCommunicate={setCommRow} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view (list) */}
          <div className="md:hidden space-y-2" data-testid="oem-mobile-list">
            {loading && <div className="py-8 text-center text-xs text-slate-400">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="py-8 text-center text-xs text-slate-400 bg-white border border-slate-200">
                No complaints yet — add one manually or bulk upload the Excel.
              </div>
            )}
            {!loading && items.map(c => (
              <div key={c.id} data-testid={`oem-card-${c.ticket_number}`}
                className="bg-white border border-slate-200 p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono font-bold text-[12px] text-slate-800 break-all">{c.ticket_number}</span>
                  <span className="text-right">
                    <StatusBadge status={c.status} />
                    {c.mails_paused && c.status !== "Resolved" && c.status !== "Client Scope" && <PausedBadge remark={c.mails_paused_remark} />}
                  </span>
                </div>
                <div className="text-[11px] text-slate-700">
                  <span className="font-semibold">{c.client_code}</span> · Store {c.store_id}
                  {c.product_id ? <span className="text-slate-500"> · {c.product_id}</span> : null}
                </div>
                {(c.issue_type || c.issue_title) && (
                  <div className="text-[11px] text-slate-700">
                    <span className="font-semibold">{c.issue_type}</span>
                    {c.issue_title ? <span className="text-slate-400"> · {c.issue_title}</span> : null}
                  </div>
                )}
                <div className="text-[10px] text-slate-500">{c.vendor_name} · {fmtDT(c.created_at_source)}</div>
                {c.vendor_complaint_number && (
                  <div className="text-[10px] text-slate-600">
                    Vendor Complaint No: <span className="font-mono font-bold">{c.vendor_complaint_number}</span>
                    {c.vendor_complaint_date && (
                      <span className="text-slate-400"> · {new Date(c.vendor_complaint_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-700">
                    <Mail size={11} className="text-slate-400" />{c.mails_sent || 0} mail(s)
                    {c.last_mail_status === "failed" && (
                      <span title={c.last_mail_error || "Last mail failed"}><AlertTriangle size={11} className="text-rose-500" /></span>
                    )}
                  </span>
                  <RowActions c={c} onView={setViewRow} onTag={setTagRow} onSendNow={sendNow}
                    onClientScope={setCsRow} onResolve={resolve} onReopen={reopen}
                    onDelete={setDeleteRow} canDelete={canDelete}
                    onPause={setPauseRow} onResume={resumeMails} onCommunicate={setCommRow} size={17} pad="p-1.5" idPrefix="oem-m" />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200">
            <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading}
              onPage={setPage} onPageSize={(n) => { setPage(1); setPageSize(n); }}
              pageSizeOptions={[25, 50, 100, 200]} />
          </div>
        </>
      )}

      {showAdd && (
        <AddComplaintModal clients={clients} vendors={vendors} members={members}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); loadStats(); }} />
      )}
      {showUpload && (
        <BulkUploadModal onClose={() => setShowUpload(false)}
          onDone={() => { load(); loadStats(); }} />
      )}
      {showBulkUpdate && (
        <BulkUpdateModal onClose={() => setShowBulkUpdate(false)}
          onDone={() => { load(); loadStats(); }} />
      )}
      {tagRow && (
        <TagModal row={tagRow} onClose={() => setTagRow(null)}
          onSaved={() => { setTagRow(null); load(); }} />
      )}
      {csRow && (
        <ClientScopeModal row={csRow} onClose={() => setCsRow(null)}
          onSaved={() => { setCsRow(null); load(); loadStats(); }} />
      )}
      {reopenRow && (
        <ReopenModal row={reopenRow} onClose={() => setReopenRow(null)}
          onSaved={() => { setReopenRow(null); load(); loadStats(); }} />
      )}
      {viewRow && <ViewModal row={viewRow} onClose={() => setViewRow(null)} />}
      {deleteRow && (
        <DeleteConfirmModal row={deleteRow} onClose={() => setDeleteRow(null)}
          onDeleted={() => { setDeleteRow(null); load(); loadStats(); }} />
      )}
      {pauseRow && (
        <PauseMailsModal row={pauseRow} onClose={() => setPauseRow(null)}
          onSaved={() => { setPauseRow(null); load(); }} />
      )}
      {commRow && (
        <CommunicationModal row={commRow} onClose={() => setCommRow(null)}
          onSaved={() => setCommRow(null)} />
      )}
    </div>
  );
}

// ─── Stop auto mails (per complaint) ────────────────────────────────────
function PauseMailsModal({ row, onClose, onSaved }) {
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post(`/oem-complaints/${row.id}/pause-mails`, { remark: remark.trim() });
      toast.success(`${row.ticket_number} — auto mails stopped (manual "Send now" still available)`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Pause failed");
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-pause-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-amber-600">
          <div className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Pause size={14} /> Stop Auto Mails
          </div>
          <div className="text-amber-100 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-700">
            The daily chase mails to the vendor will <span className="font-bold">stop</span> for this
            complaint only. Manual "Send now" stays available. You can restart anytime — restart is
            fresh (no backlog mails), the next mail goes at the next configured daily slot.
          </p>
          <MField label="Remark (optional — kept on the complaint)">
            <textarea data-testid="oem-pause-remark" rows={3} value={remark} onChange={e => setRemark(e.target.value)}
              placeholder="Why are mails being stopped…" className={`${inputCls} resize-none`} autoFocus />
          </MField>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-pause-cancel" onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-pause-confirm" onClick={save} disabled={saving}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Stopping…" : "Stop Auto Mails"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Communication (manual update log) ──────────────────────────────
function CommunicationModal({ row, onClose, onSaved }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!remark.trim()) { toast.error("Remark is required"); return; }
    setSaving(true);
    try {
      await api.post(`/oem-complaints/${row.id}/communications`, { date, remark: remark.trim() });
      toast.success(`${row.ticket_number} — communication added`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-comm-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-teal-600">
          <div className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <MessageSquarePlus size={14} /> Add Communication
          </div>
          <div className="text-teal-100 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <MField label="Date">
            <input type="date" data-testid="oem-comm-date" value={date}
              onChange={e => setDate(e.target.value)} className={inputCls} />
          </MField>
          <MField label={`Update / Remark (${remark.length}/500)`}>
            <textarea data-testid="oem-comm-remark" rows={4} maxLength={500} value={remark}
              onChange={e => setRemark(e.target.value)}
              placeholder="e.g. Spoke to vendor service head — engineer visit promised by Friday…"
              className={`${inputCls} resize-none`} autoFocus />
          </MField>
          <p className="text-[10px] text-slate-400">
            You can add as many communications as needed — all are kept and shown in the
            View popup (Communications tab) and the detailed Excel report.
          </p>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-comm-cancel" onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-comm-save" onClick={save} disabled={saving}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Saving…" : "Save Communication"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Permanent delete confirmation ──────────────────────────────────────
function DeleteConfirmModal({ row, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  async function doDelete() {
    setDeleting(true);
    try {
      await api.delete(`/oem-complaints/${row.id}`);
      toast.success(`${row.ticket_number} deleted permanently`);
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-delete-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-rose-700">
          <div className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Trash2 size={14} /> Delete Complaint
          </div>
          <div className="text-rose-100 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name}</div>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-slate-700" data-testid="oem-delete-warning">
            This will <span className="font-bold text-rose-700">permanently delete</span> complaint{" "}
            <span className="font-mono font-bold">{row.ticket_number}</span> — including its full mail
            history. No further mails, no counts, no trace. This cannot be undone.
          </p>
          <p className="text-[11px] text-slate-500">
            The same ticket number can be uploaded again as a new complaint afterwards.
          </p>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-delete-cancel" onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-delete-confirm" onClick={doDelete} disabled={deleting}
            className="px-4 py-2 bg-rose-700 hover:bg-rose-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {deleting ? "Deleting…" : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Figures / dashboard ────────────────────────────────────────────────
// Branded header rendered inside every PNG-shareable section — looks good
// on screen AND makes the downloaded PNG client-presentable.
function PngBanner({ title, subtitle, today }) {
  return (
    <div className="png-banner">
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
        <div>
          <div className="png-banner-title text-[16px] font-extrabold text-[#0B1F3A] tracking-tight leading-tight">{title}</div>
          <div className="png-banner-sub text-[11px] font-semibold text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="png-banner-tag text-[10px] font-extrabold text-[#F97316] uppercase tracking-[0.22em]">OEM Complaint Report</div>
          <div className="png-banner-date text-[12px] font-bold text-slate-700 mt-0.5">{today}</div>
        </div>
      </div>
      <div className="h-[3px] mx-5 mb-2 bg-gradient-to-r from-[#0B1F3A] via-[#F97316] to-[#F97316]/10" />
    </div>
  );
}

function FiguresView({ stats, onRefresh }) {
  const vendorRef = useRef(null);
  const pendingRef = useRef(null);
  const ageingRef = useRef(null);

  async function downloadPng(node, filename) {
    if (!node) return;
    try {
      // Jun-2026 — client-ready capture: the `oem-png-capture` class bumps
      // heading sizes + bolds cell text for the image only; measured AFTER
      // the class applies so nothing clips. Overflow made visible — no
      // scrollbars, no right-side clipping. pixelRatio 2 keeps text crisp.
      node.classList.add("oem-png-capture");
      await new Promise((r) => setTimeout(r, 60));
      const w = node.scrollWidth;
      const h = node.scrollHeight;
      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        cacheBust: true,
        width: w,
        height: h,
        style: { overflow: "visible", width: `${w}px`, height: `${h}px` },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      a.click();
      toast.success("PNG downloaded — ready to paste in WhatsApp / mail");
    } catch {
      toast.error("Could not capture the image — please retry");
    } finally {
      node.classList.remove("oem-png-capture");
    }
  }
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  if (!stats) return <div className="py-10 text-center text-xs text-slate-400" data-testid="oem-figures-loading">Loading figures…</div>;
  const cards = [
    { label: "Total Complaints", value: stats.total, cls: "text-slate-800" },
    { label: "Resolved", value: stats.resolved, cls: "text-emerald-600" },
    { label: "Pending", value: stats.pending, cls: "text-amber-600" },
    { label: "Client Scope", value: stats.client_scope || 0, cls: "text-violet-600" },
    { label: "Total Mails Sent", value: stats.mails_sent_total, cls: "text-sky-600" },
  ];
  const AGE_BUCKETS = ["0-2 days", "3-7 days", "8-15 days", "15+ days"];
  const AGE_COLORS = {
    "0-2 days":  { th: "text-emerald-700 bg-emerald-50", td: "text-emerald-700" },
    "3-7 days":  { th: "text-amber-700 bg-amber-50",     td: "text-amber-700" },
    "8-15 days": { th: "text-orange-700 bg-orange-50",   td: "text-orange-700" },
    "15+ days":  { th: "text-rose-700 bg-rose-50",       td: "text-rose-700" },
  };
  const pendingVendors = (stats.by_vendor || []).filter(v => (v.pending || 0) > 0);
  return (
    <div className="space-y-4" data-testid="oem-figures">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white border border-slate-200 p-4" data-testid={`oem-stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{c.label}</div>
            <div className={`text-2xl font-bold mt-1 tabular-nums ${c.cls}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ── Vendor-wise (PNG-shareable) ─────────────────────────── */}
      <div className="bg-white border border-slate-200/70 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100/80 flex items-center justify-between gap-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold bg-gradient-to-r from-[#0B1F3A] to-[#F97316] bg-clip-text text-transparent">
            Vendor-wise
          </h3>
          <div className="flex items-center gap-1.5">
            <button onClick={() => downloadPng(vendorRef.current, `OEM_Vendor_Wise_${today.replace(/ /g, "-")}.png`)}
              data-testid="oem-vendor-png-btn"
              className="flex items-center gap-1 border border-[#F97316]/60 text-[#F97316] hover:bg-[#F97316] hover:text-white px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold">
              <Download size={11} /> PNG
            </button>
            <button onClick={onRefresh} className="text-slate-400 hover:text-slate-700 p-1.5" title="Refresh"><RotateCw size={13} /></button>
          </div>
        </div>
        <div className="overflow-x-auto" ref={vendorRef}>
          <PngBanner title="OEM Complaints — Vendor-wise Summary"
            subtitle="All complaints grouped by vendor · totals, resolved, pending & mails" today={today} />
          <table className="w-full min-w-[620px]" data-testid="oem-vendor-table">
            <thead>
              <tr className="border-b border-slate-200/70 text-left text-[10px] uppercase tracking-wider">
                <th className="py-2 px-3 font-bold text-slate-600 bg-slate-50">Vendor</th>
                <th className="py-2 px-3 font-bold text-right text-slate-700 bg-slate-100/70">Total</th>
                <th className="py-2 px-3 font-bold text-right text-emerald-700 bg-emerald-50">Resolved</th>
                <th className="py-2 px-3 font-bold text-right text-amber-700 bg-amber-50">Pending</th>
                <th className="py-2 px-3 font-bold text-right text-indigo-700 bg-indigo-50">Number of Vendor Complaint No Received</th>
                <th className="py-2 px-3 font-bold text-right text-sky-700 bg-sky-50">Mails Sent</th>
              </tr>
            </thead>
            <tbody>
              {(stats.by_vendor || []).length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-xs text-slate-400">No data yet.</td></tr>
              )}
              {(stats.by_vendor || []).map((v, i) => (
                <tr key={i} className="border-b border-slate-100/60 text-[11px]">
                  <td className="py-2 px-3 text-slate-700 font-semibold">{v.vendor_name} {v.vendor_code ? <span className="text-slate-400 font-normal">({v.vendor_code})</span> : null}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold">{v.total}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-emerald-600">{v.resolved}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-amber-600">{v.pending}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-indigo-600 font-semibold" data-testid={`oem-vendor-tagged-${i}`}>{v.tagged || 0}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-sky-600">{v.mails_sent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pending only — Vendor-wise (PNG-shareable) ───────────── */}
      <div className="bg-white border border-slate-200/70 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100/80 flex items-center justify-between gap-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold bg-gradient-to-r from-[#0B1F3A] to-[#F97316] bg-clip-text text-transparent">
            Pending — Vendor-wise
          </h3>
          <button onClick={() => downloadPng(pendingRef.current, `OEM_Pending_Vendor_Wise_${today.replace(/ /g, "-")}.png`)}
            data-testid="oem-pending-vendor-png-btn"
            className="flex items-center gap-1 border border-[#F97316]/60 text-[#F97316] hover:bg-[#F97316] hover:text-white px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold">
            <Download size={11} /> PNG
          </button>
        </div>
        <div className="overflow-x-auto" ref={pendingRef}>
          <PngBanner title="OEM Pending Complaints — Vendor-wise Summary"
            subtitle="Open complaints awaiting vendor resolution" today={today} />
          <table className="w-full min-w-[620px]" data-testid="oem-pending-vendor-table">
            <thead>
              <tr className="border-b border-slate-200/70 text-left text-[10px] uppercase tracking-wider">
                <th className="py-2 px-3 font-bold text-slate-600 bg-slate-50">Vendor</th>
                <th className="py-2 px-3 font-bold text-right text-amber-700 bg-amber-50">Pending</th>
                <th className="py-2 px-3 font-bold text-right text-indigo-700 bg-indigo-50">Number of Vendor Complaint No Received</th>
                <th className="py-2 px-3 font-bold text-right text-rose-700 bg-rose-50">Vendor Complaint No Not Received</th>
                <th className="py-2 px-3 font-bold text-right text-sky-700 bg-sky-50">Mails Sent</th>
              </tr>
            </thead>
            <tbody>
              {pendingVendors.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-xs text-slate-400">No pending complaints — all clear.</td></tr>
              )}
              {pendingVendors.map((v, i) => (
                <tr key={i} className="border-b border-slate-100/60 text-[11px]">
                  <td className="py-2 px-3 text-slate-700 font-semibold">{v.vendor_name} {v.vendor_code ? <span className="text-slate-400 font-normal">({v.vendor_code})</span> : null}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-amber-600">{v.pending}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-indigo-600 font-semibold" data-testid={`oem-pending-tagged-${i}`}>{v.pending_tagged || 0}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-rose-600 font-semibold">{(v.pending || 0) - (v.pending_tagged || 0)}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-sky-600">{v.pending_mails || 0}</td>
                </tr>
              ))}
              {pendingVendors.length > 0 && (
                <tr className="text-[11px] bg-slate-50/80 border-t border-slate-200/70">
                  <td className="py-2 px-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">Total</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-amber-700">{pendingVendors.reduce((s, v) => s + (v.pending || 0), 0)}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-indigo-700">{pendingVendors.reduce((s, v) => s + (v.pending_tagged || 0), 0)}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-rose-700">{pendingVendors.reduce((s, v) => s + ((v.pending || 0) - (v.pending_tagged || 0)), 0)}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-sky-700">{pendingVendors.reduce((s, v) => s + (v.pending_mails || 0), 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pending — Ageing, vendor-wise (PNG-shareable) ────────── */}
      <div className="bg-white border border-slate-200/70 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100/80 flex items-center justify-between gap-2">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold bg-gradient-to-r from-[#0B1F3A] to-[#F97316] bg-clip-text text-transparent">
            Pending — Ageing (Vendor-wise)
          </h3>
          <button onClick={() => downloadPng(ageingRef.current, `OEM_Pending_Ageing_${today.replace(/ /g, "-")}.png`)}
            data-testid="oem-ageing-png-btn"
            className="flex items-center gap-1 border border-[#F97316]/60 text-[#F97316] hover:bg-[#F97316] hover:text-white px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold">
            <Download size={11} /> PNG
          </button>
        </div>
        <div className="overflow-x-auto" ref={ageingRef} data-testid="oem-ageing">
          <PngBanner title="OEM Pending Complaints — Ageing by Vendor"
            subtitle="How long each vendor's open complaints have been waiting" today={today} />
          <table className="w-full min-w-[560px]" data-testid="oem-ageing-table">
            <thead>
              <tr className="border-b border-slate-200/70 text-left text-[10px] uppercase tracking-wider">
                <th className="py-2 px-3 font-bold text-slate-600 bg-slate-50">Vendor</th>
                {AGE_BUCKETS.map(b => (
                  <th key={b} className={`py-2 px-3 font-bold text-right ${AGE_COLORS[b].th}`}>{b}</th>
                ))}
                <th className="py-2 px-3 font-bold text-right text-slate-700 bg-slate-100/70">Total Pending</th>
              </tr>
            </thead>
            <tbody>
              {pendingVendors.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-xs text-slate-400">No pending complaints — all clear.</td></tr>
              )}
              {pendingVendors.map((v, i) => (
                <tr key={i} className="border-b border-slate-100/60 text-[11px]">
                  <td className="py-2 px-3 text-slate-700 font-semibold">{v.vendor_name} {v.vendor_code ? <span className="text-slate-400 font-normal">({v.vendor_code})</span> : null}</td>
                  {AGE_BUCKETS.map(b => (
                    <td key={b} className={`py-2 px-3 text-right tabular-nums font-semibold ${((v.ageing || {})[b] || 0) > 0 ? AGE_COLORS[b].td : "text-slate-300"}`}>
                      {(v.ageing || {})[b] || 0}
                    </td>
                  ))}
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-slate-800">{v.pending}</td>
                </tr>
              ))}
              {pendingVendors.length > 0 && (
                <tr className="text-[11px] bg-slate-50/80 border-t border-slate-200/70">
                  <td className="py-2 px-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">Total</td>
                  {AGE_BUCKETS.map(b => (
                    <td key={b} className={`py-2 px-3 text-right tabular-nums font-bold ${AGE_COLORS[b].td}`}>
                      {(stats.ageing || {})[b] || 0}
                    </td>
                  ))}
                  <td className="py-2 px-3 text-right tabular-nums font-bold text-slate-900">{stats.pending}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Manual entry ───────────────────────────────────────────────────────
function AddComplaintModal({ clients, vendors, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_code: "", ticket_number: "", product_id: "", created_at_source: "",
    store_id: "", category: "", sub_category: "", issue_type: "", issue_title: "",
    afm_name: "", supervisor_code: "", vendor_code: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const client = useMemo(
    () => clients.find(c => (c.client_code || "").toLowerCase() === form.client_code.toLowerCase()),
    [clients, form.client_code]);
  const stores = useMemo(() => {
    const seen = new Set();
    return (client?.shipping_addresses || []).filter(s => {
      const code = (s.code || "").trim();
      if (!code || seen.has(code.toLowerCase())) return false;
      seen.add(code.toLowerCase());
      return true;
    });
  }, [client]);
  const pocs = client?.pocs || [];

  async function save() {
    const required = { client_code: "Client", ticket_number: "Ticket Number", created_at_source: "Created At", store_id: "Store ID", afm_name: "AFM Name", supervisor_code: "Supervisor", vendor_code: "Vendor" };
    for (const [k, label] of Object.entries(required)) {
      if (!String(form[k] || "").trim()) { toast.error(`${label} is required`); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/oem-complaints", form);
      toast.success(`Complaint ${data.ticket_number} saved — mail is being sent to ${data.vendor_name}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-add-modal">
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A] flex items-center justify-between">
          <div className="text-white font-bold text-sm uppercase tracking-wider">New OEM Complaint</div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="oem-add-close"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <MField label="Client *">
            <SearchableSelect
              value={form.client_code}
              onChange={(code) => setForm(f => ({ ...f, client_code: code || "", store_id: "", afm_name: "" }))}
              items={clients}
              getKey={(c) => c.client_code}
              getLabel={(c) => `${c.client_code} · ${c.name}`}
              getSubLabel={(c) => c.gst_number || ""}
              placeholder="Search & select client…"
              testid="oem-add-client"
            />
          </MField>
          <MField label="Ticket Number * (unique)">
            <input data-testid="oem-add-ticket" value={form.ticket_number} onChange={e => set("ticket_number", e.target.value)} className={inputCls} placeholder="CM1767852411461" />
          </MField>
          <MField label="Created At * (client-side complaint time)">
            <input data-testid="oem-add-created" type="datetime-local" value={form.created_at_source} onChange={e => set("created_at_source", e.target.value)} className={inputCls} />
          </MField>
          <MField label="Store ID *">
            <SearchableSelect
              value={form.store_id}
              onChange={(code) => set("store_id", code || "")}
              items={stores}
              getKey={(s) => s.code}
              getLabel={(s) => `${s.code} · ${s.label}`}
              getSubLabel={(s) => [s.city, s.state].filter(Boolean).join(", ")}
              placeholder={client ? (stores.length ? "Search & select store code…" : "No shipping-address codes on this client") : "Pick a client first"}
              disabled={!client}
              testid="oem-add-store"
            />
          </MField>
          <MField label="Product Id"><input data-testid="oem-add-product" value={form.product_id} onChange={e => set("product_id", e.target.value)} className={inputCls} /></MField>
          <MField label="AFM Name * (Client POC)">
            <SearchableSelect
              value={form.afm_name}
              onChange={(name) => set("afm_name", name || "")}
              items={pocs}
              getKey={(p) => p.name}
              getLabel={(p) => p.name}
              getSubLabel={(p) => [p.designation, p.phone, p.email].filter(Boolean).join(" · ")}
              placeholder={client ? (pocs.length ? "Search & select POC…" : "No POCs on this client") : "Pick a client first"}
              disabled={!client}
              testid="oem-add-afm"
            />
          </MField>
          <MField label="Supervisor * (Member)">
            <SearchableSelect
              value={form.supervisor_code}
              onChange={(code) => set("supervisor_code", code || "")}
              items={members}
              getKey={(m) => m.member_code}
              getLabel={(m) => `${m.member_code} · ${m.name}`}
              getSubLabel={(m) => [m.designation, m.phone].filter(Boolean).join(" · ")}
              placeholder="Search & select member…"
              testid="oem-add-supervisor"
            />
          </MField>
          <MField label="Actual Vendor *">
            <SearchableSelect
              value={form.vendor_code}
              onChange={(code) => set("vendor_code", code || "")}
              items={vendors}
              getKey={(v) => v.vendor_code}
              getLabel={(v) => `${v.vendor_code} · ${v.name}`}
              getSubLabel={(v) => v.email || ""}
              placeholder="Search & select vendor…"
              testid="oem-add-vendor"
            />
          </MField>
          <MField label="Category"><input data-testid="oem-add-category" value={form.category} onChange={e => set("category", e.target.value)} className={inputCls} placeholder="HVAC" /></MField>
          <MField label="Sub Category"><input data-testid="oem-add-subcategory" value={form.sub_category} onChange={e => set("sub_category", e.target.value)} className={inputCls} placeholder="HVAC_DUCIN" /></MField>
          <MField label="Issue Type"><input data-testid="oem-add-issuetype" value={form.issue_type} onChange={e => set("issue_type", e.target.value)} className={inputCls} placeholder="AC NOT WORKING" /></MField>
          <MField label="Issue Title"><input data-testid="oem-add-issuetitle" value={form.issue_title} onChange={e => set("issue_title", e.target.value)} className={inputCls} /></MField>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-add-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-add-save" onClick={save} disabled={saving}
            className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Saving…" : "Save & Send Mail"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk upload ────────────────────────────────────────────────────────
function BulkUploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function downloadTemplate() {
    try {
      const res = await api.get("/oem-complaints/bulk-upload-template", { responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "OEM_Complaints_Bulk_Upload_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill the 'Complaints' sheet and upload it back");
    } catch {
      toast.error("Template download failed");
    }
  }

  async function upload() {
    if (!file) { toast.error("Choose the Excel file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/oem-complaints/bulk-upload", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(data);
      if (data.inserted) toast.success(`${data.inserted} complaint(s) uploaded — vendor mails are being sent`);
      if (data.rejected) toast.warning(`${data.rejected} row(s) rejected — see details below`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-upload-modal">
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A] flex items-center justify-between">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Bulk Upload — Client Complaints</div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="oem-upload-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 p-3">
            Excel columns (matched by header name): <b>Client Name (Code)</b>, <b>Ticket Number</b>,
            <b> Product Id</b>, <b>Created At</b>, <b>Store ID</b>, Category, Sub Category, Issue Type,
            Issue Title, <b>AFM Name</b> (client POC), <b>Supervisor Name</b> (member code),
            <b> Actual Vendor</b> (vendor code). Rows failing validation are rejected with the reason —
            valid rows are saved and the vendor mail fires immediately.
            <button type="button" data-testid="oem-upload-template-btn" onClick={downloadTemplate}
              className="mt-2 flex items-center gap-1.5 border border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold">
              <Download size={11} /> Download Template
            </button>
          </div>
          <input type="file" accept=".xlsx,.xlsm" data-testid="oem-upload-file"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs text-slate-600 file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-[#0B1F3A] file:text-white file:text-[11px] file:uppercase file:font-bold file:tracking-wider" />
          <div className="flex justify-end gap-2">
            <button data-testid="oem-upload-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Close</button>
            <button data-testid="oem-upload-go" onClick={upload} disabled={busy || !file}
              className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
              {busy ? "Uploading…" : "Upload & Validate"}
            </button>
          </div>
          {result && (
            <div className="space-y-2" data-testid="oem-upload-result">
              <div className="text-xs font-bold text-slate-700">
                <span className="text-emerald-600">{result.inserted} inserted</span>
                {" · "}
                <span className={result.rejected ? "text-rose-600" : "text-slate-400"}>{result.rejected} rejected</span>
              </div>
              {(result.rejected_rows || []).length > 0 && (
                <div className="border border-rose-200 bg-rose-50 max-h-64 overflow-y-auto">
                  {(result.rejected_rows || []).map((r, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-rose-100 text-[11px]">
                      <b>Row {r.row}</b>{r.ticket_number ? ` · ${r.ticket_number}` : ""} —{" "}
                      <span className="text-rose-700">{(r.errors || []).join("; ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Bulk update (tag / resolve via Excel) ──────────────────────────────
function BulkUpdateModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function downloadTemplate() {
    try {
      const res = await api.get("/oem-complaints/bulk-update-template", { responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "OEM_Complaints_Bulk_Update_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill the 'Updates' sheet and upload it back");
    } catch {
      toast.error("Template download failed");
    }
  }

  async function upload() {
    if (!file) { toast.error("Choose the Excel file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/oem-complaints/bulk-update", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(data);
      if (data.updated) toast.success(`${data.updated} complaint(s) updated — ${data.tagged} tagged · ${data.resolved} resolved`);
      if (data.rejected) toast.warning(`${data.rejected} row(s) rejected — see details below`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk update failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-bulkupdate-modal">
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A] flex items-center justify-between">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Bulk Update — Tag / Resolve</div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="oem-bulkupdate-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 p-3">
            Excel columns (matched by header name, one row per ticket):
            <ul className="list-disc ml-4 mt-1 space-y-0.5">
              <li><b>Ticket Number</b> — required, must exist in the register</li>
              <li><b>Vendor Complaint Number</b> — optional, tags/updates the vendor&apos;s register number</li>
              <li><b>Vendor Complaint Date</b> — optional, date the vendor registered the call in their portal</li>
              <li><b>Resolved</b> — optional, write <code>Yes</code> / <code>Y</code> / <code>Resolved</code> to mark resolved (stops mails)</li>
            </ul>
            Blank cells leave that field unchanged.
            <button type="button" data-testid="oem-bulkupdate-template-btn" onClick={downloadTemplate}
              className="mt-2 flex items-center gap-1.5 border border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold">
              <Download size={11} /> Download Template
            </button>
          </div>
          <input type="file" accept=".xlsx,.xlsm" data-testid="oem-bulkupdate-file"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs text-slate-600 file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-[#0B1F3A] file:text-white file:text-[11px] file:uppercase file:font-bold file:tracking-wider" />
          <div className="flex justify-end gap-2">
            <button data-testid="oem-bulkupdate-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Close</button>
            <button data-testid="oem-bulkupdate-go" onClick={upload} disabled={busy || !file}
              className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
              {busy ? "Updating…" : "Upload & Update"}
            </button>
          </div>
          {result && (
            <div className="space-y-2" data-testid="oem-bulkupdate-result">
              <div className="text-xs font-bold text-slate-700">
                <span className="text-emerald-600">{result.updated} updated</span>
                {" · "}<span className="text-sky-600">{result.tagged} tagged</span>
                {" · "}<span className="text-emerald-700">{result.resolved} resolved</span>
                {" · "}<span className={result.rejected ? "text-rose-600" : "text-slate-400"}>{result.rejected} rejected</span>
              </div>
              {(result.details || []).length > 0 && (
                <div className="border border-emerald-200 bg-emerald-50 max-h-40 overflow-y-auto">
                  {(result.details || []).map((r, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-emerald-100 text-[11px]">
                      <b>Row {r.row}</b> · {r.ticket_number} — <span className="text-emerald-700">{r.changes}</span>
                    </div>
                  ))}
                </div>
              )}
              {(result.rejected_rows || []).length > 0 && (
                <div className="border border-rose-200 bg-rose-50 max-h-40 overflow-y-auto">
                  {(result.rejected_rows || []).map((r, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-rose-100 text-[11px]">
                      <b>Row {r.row}</b>{r.ticket_number ? ` · ${r.ticket_number}` : ""} —{" "}
                      <span className="text-rose-700">{(r.errors || []).join("; ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Client Scope summary + Excel export ────────────────────────────────
function ClientScopeView({ stats, onRefresh }) {
  const [exporting, setExporting] = useState(false);
  if (!stats) return <div className="py-10 text-center text-xs text-slate-400" data-testid="oem-cs-loading">Loading…</div>;
  const vendorRows = (stats.by_vendor || []).filter(v => (v.client_scope || 0) > 0);

  async function exportExcel() {
    setExporting(true);
    try {
      const res = await api.get("/oem-complaints/client-scope-export", { responseType: "blob", timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `OEM_Client_Scope_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Client Scope register downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  }

  return (
    <div className="space-y-4" data-testid="oem-clientscope-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">
          Complaints moved out of vendor scope — mails are stopped until they are marked Open again.
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5" data-testid="oem-cs-refresh">
            <RotateCw size={12} /> Refresh
          </button>
          <button onClick={exportExcel} disabled={exporting} data-testid="oem-cs-export-btn"
            className="bg-[#0B1F3A] hover:bg-[#12294a] text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50">
            <Download size={12} /> {exporting ? "Exporting…" : "Export to Excel"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 p-4" data-testid="oem-cs-stat-count">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Client Scope Complaints</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-violet-600">{stats.client_scope || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="oem-cs-stat-amount">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total Amount</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-slate-800">{formatINR(stats.cs_amount_total || 0)}</div>
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="oem-cs-stat-vendors">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Vendors Involved</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-sky-600">{vendorRows.length}</div>
        </div>
      </div>
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Client Scope — Vendor-wise</h3>
        </div>
        <table className="w-full" data-testid="oem-cs-vendor-table">
          <thead>
            <tr className="border-b border-slate-100 text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-2 px-3 font-bold">Vendor</th>
              <th className="py-2 px-3 font-bold text-right">Client Scope Calls</th>
              <th className="py-2 px-3 font-bold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {vendorRows.length === 0 && (
              <tr><td colSpan={3} className="py-6 text-center text-xs text-slate-400" data-testid="oem-cs-empty">No Client Scope complaints yet.</td></tr>
            )}
            {vendorRows.map((v, i) => (
              <tr key={i} className="border-b border-slate-50 text-[11px]">
                <td className="py-2 px-3 text-slate-700 font-semibold">{v.vendor_name} {v.vendor_code ? <span className="text-slate-400 font-normal">({v.vendor_code})</span> : null}</td>
                <td className="py-2 px-3 text-right tabular-nums font-bold text-violet-600">{v.client_scope}</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatINR(v.cs_amount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Client Scope modal (reason master + amount + remark) ───────────────
function ClientScopeModal({ row, onClose, onSaved }) {
  const [reasons, setReasons] = useState(null);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [newReason, setNewReason] = useState("");
  const [manage, setManage] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadReasons = () =>
    api.get("/oem-complaints/cs-reasons").then(r => setReasons(r.data || [])).catch(() => setReasons([]));
  useEffect(() => { loadReasons(); }, []); // eslint-disable-line

  async function addReason() {
    const name = newReason.trim();
    if (!name) return;
    try {
      await api.post("/oem-complaints/cs-reasons", { name });
      setNewReason("");
      await loadReasons();
      setReason(name);
      toast.success(`Reason '${name}' added to the master`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add reason");
    }
  }

  async function deleteReason(r) {
    try {
      await api.delete(`/oem-complaints/cs-reasons/${r.id}`);
      if (reason === r.name) setReason("");
      await loadReasons();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  }

  async function save() {
    if (!reason) { toast.error("Select a Reason"); return; }
    setSaving(true);
    try {
      await api.post(`/oem-complaints/${row.id}/client-scope`,
        { reason, amount: amount === "" ? null : amount, remark });
      toast.success(`${row.ticket_number} marked Client Scope — vendor mails stopped`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-cs-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A]">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Mark as Client Scope</div>
          <div className="text-slate-300 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name} — vendor mails will STOP</div>
        </div>
        <div className="p-5 space-y-3">
          <MField label="Reason *">
            <div className="flex gap-2">
              <select data-testid="oem-cs-reason" value={reason} onChange={e => setReason(e.target.value)}
                className="flex-1 px-2.5 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white">
                <option value="">{reasons === null ? "Loading…" : "— Select reason —"}</option>
                {(reasons || []).map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
              <button type="button" data-testid="oem-cs-manage-btn" onClick={() => setManage(m => !m)}
                className="border border-slate-300 text-slate-600 hover:bg-slate-50 px-2.5 py-2 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap">
                {manage ? "Done" : "Manage"}
              </button>
            </div>
          </MField>
          {manage && (
            <div className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="oem-cs-reason-master">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Reason Master</div>
              {(reasons || []).map(r => (
                <div key={r.id} className="flex items-center justify-between gap-2 text-[11px] bg-white border border-slate-200 px-2.5 py-1.5">
                  <span className="text-slate-700">{r.name}</span>
                  <button type="button" data-testid={`oem-cs-reason-del-${r.id}`} onClick={() => deleteReason(r)}
                    className="text-rose-500 hover:text-rose-700" title="Delete reason"><Trash2 size={12} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input data-testid="oem-cs-reason-new" value={newReason} onChange={e => setNewReason(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addReason(); } }}
                  placeholder="New reason…" className="flex-1 px-2.5 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white" />
                <button type="button" data-testid="oem-cs-reason-add" onClick={addReason}
                  className="border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                  <Plus size={11} /> Add
                </button>
              </div>
            </div>
          )}
          <MField label="Amount (optional)">
            <input data-testid="oem-cs-amount" type="number" step="0.01" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
          </MField>
          <MField label="Remark (optional)">
            <textarea data-testid="oem-cs-remark" rows={3} value={remark} onChange={e => setRemark(e.target.value)}
              className={`${inputCls} resize-none`} />
          </MField>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-cs-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-cs-save" onClick={save} disabled={saving}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Saving…" : "Mark Client Scope"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Reopen from Client Scope (remark required → quoted in next mails) ──
function ReopenModal({ row, onClose, onSaved }) {
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!remark.trim()) { toast.error("Remark is required — it is quoted in the next vendor mails"); return; }
    setSaving(true);
    try {
      await api.post(`/oem-complaints/${row.id}/reopen`, { remark: remark.trim() });
      toast.success(`${row.ticket_number} moved back to Open — mails resume and will mention your remark`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reopen failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-reopen-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A]">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Move Back to Open</div>
          <div className="text-slate-300 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name} — mails will resume</div>
        </div>
        <div className="p-5">
          <MField label="Remark * (quoted in the next vendor mails)">
            <textarea data-testid="oem-reopen-remark" rows={3} value={remark} onChange={e => setRemark(e.target.value)}
              placeholder="Why is this back in the vendor's scope…" className={`${inputCls} resize-none`} autoFocus />
          </MField>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-reopen-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-reopen-save" onClick={save} disabled={saving}
            className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Saving…" : "Reopen & Resume Mails"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tag vendor complaint number ────────────────────────────────────────
function TagModal({ row, onClose, onSaved }) {
  const [num, setNum] = useState(row.vendor_complaint_number || "");
  const [date, setDate] = useState((row.vendor_complaint_date || "").slice(0, 10));
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!num.trim()) { toast.error("Enter the Vendor Complaint Register Number"); return; }
    setSaving(true);
    try {
      await api.put(`/oem-complaints/${row.id}`, {
        vendor_complaint_number: num.trim(),
        vendor_complaint_date: date || "",
      });
      toast.success("Vendor Complaint Number tagged — next mails will quote it in subject & body");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-tag-modal">
      <div className="bg-white w-full max-w-md shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A]">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Tag Vendor Complaint Number</div>
          <div className="text-slate-300 text-[11px] mt-0.5">Ticket {row.ticket_number} · {row.vendor_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <MField label="Vendor Complaint Register Number *">
            <input data-testid="oem-tag-input" value={num} onChange={e => setNum(e.target.value)}
              placeholder="Vendor's complaint register number" className={inputCls} autoFocus />
          </MField>
          <MField label="Vendor Complaint Date (registered in vendor's portal)">
            <input data-testid="oem-tag-date" type="date" value={date}
              onChange={e => setDate(e.target.value)} className={inputCls} />
          </MField>
        </div>
        <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2">
          <button data-testid="oem-tag-cancel" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="oem-tag-save" onClick={save} disabled={saving}
            className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── View details + mail history + communications ───────────────────────
function ViewModal({ row, onClose }) {
  const [logs, setLogs] = useState(null);
  const [comms, setComms] = useState(null);
  const [vtab, setVtab] = useState("mails"); // mails | comms
  useEffect(() => {
    api.get(`/oem-complaints/${row.id}/emails`).then(r => setLogs(r.data || [])).catch(() => setLogs([]));
    api.get(`/oem-complaints/${row.id}/communications`).then(r => setComms(r.data || [])).catch(() => setComms([]));
  }, [row.id]);

  const rows = [
    ["Ticket Number", row.ticket_number], ["Status", row.status === "Mail Sent" ? "Register Mail Sent" : row.status],
    ["Client", `${row.client_name} (${row.client_code})`], ["Store", `${row.store_id}${row.store_label ? ` · ${row.store_label}` : ""}`],
    ["Store Address", row.store_address], ["Store Email (CC)", row.store_email || "— none on file —"],
    ["Product Id", row.product_id], ["Created At", fmtDT(row.created_at_source)],
    ["Category", row.category], ["Sub Category", row.sub_category],
    ["Issue Type", row.issue_type], ["Issue Title", row.issue_title],
    ["AFM (Client POC)", `${row.afm_name}${row.afm_phone ? ` · ${row.afm_phone}` : ""}`],
    ["Supervisor", `${row.supervisor_name} (${row.supervisor_code})${row.supervisor_phone ? ` · ${row.supervisor_phone}` : ""}`],
    ["Vendor", `${row.vendor_name} (${row.vendor_code})`],
    ["Vendor Complaint No", row.vendor_complaint_number || "— not tagged yet —"],
    ["Vendor Complaint Date", row.vendor_complaint_date
      ? new Date(row.vendor_complaint_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
      : "—"],
    ...(row.client_scope_reason ? [
      ["Client Scope Reason", row.client_scope_reason],
      ["Client Scope Amount", row.client_scope_amount != null ? formatINR(row.client_scope_amount) : "—"],
      ["Client Scope Remark", row.client_scope_remark || "—"],
    ] : []),
    ...(row.reopen_remark ? [["Reopen Remark (quoted in mails)", row.reopen_remark]] : []),
    ["Mails Sent", String(row.mails_sent || 0)], ["Last Mail", row.last_mail_at ? `${fmtDT(row.last_mail_at)} (${row.last_mail_status})` : "—"],
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="oem-view-modal">
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A] flex items-center justify-between">
          <div className="text-white font-bold text-sm uppercase tracking-wider">Complaint {row.ticket_number}</div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="oem-view-close"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 whitespace-nowrap">{k}</span>
              <span className="text-[11px] text-slate-700 text-right">{v || "—"}</span>
            </div>
          ))}
        </div>
        <div className="px-5 pb-5">
          <div className="flex gap-1 mb-2 border-b border-slate-200">
            {[["mails", `Mail History${logs ? ` (${logs.length})` : ""}`],
              ["comms", `Communications${comms ? ` (${comms.length})` : ""}`]].map(([k, lbl]) => (
              <button key={k} data-testid={`oem-view-tab-${k}`} onClick={() => setVtab(k)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border-b-2 -mb-px ${vtab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                {lbl}
              </button>
            ))}
          </div>
          {vtab === "mails" && (<>
          {logs === null && <div className="text-xs text-slate-400">Loading…</div>}
          {logs !== null && logs.length === 0 && <div className="text-xs text-slate-400" data-testid="oem-view-no-mails">No mails yet.</div>}
          {(logs || []).map((l, i) => (
            <div key={i} className="border border-slate-200 px-3 py-2 mb-1.5 text-[11px]" data-testid={`oem-maillog-${i}`}>
              <div className="flex justify-between gap-2">
                <span className={`font-bold uppercase text-[9px] tracking-wider ${l.status === "sent" ? "text-emerald-600" : l.status === "failed" ? "text-rose-600" : "text-amber-600"}`}>
                  {l.status}{l.reminder_number ? ` · Reminder ${l.reminder_number}` : " · Initial"}
                </span>
                <span className="text-slate-400">{fmtDT(l.at)}</span>
              </div>
              <div className="text-slate-700 mt-0.5 truncate" title={l.subject}>{l.subject || "—"}</div>
              <div className="text-slate-400 font-mono truncate" title={`To: ${(l.to || []).join(", ")} · CC: ${(l.cc || []).join(", ")}`}>
                To: {(l.to || []).join(", ") || "—"}{(l.cc || []).length ? ` · CC: ${(l.cc || []).join(", ")}` : ""}
              </div>
              {l.failure_reason && <div className="text-rose-600 mt-0.5">{l.failure_reason}</div>}
            </div>
          ))}
          </>)}
          {vtab === "comms" && (<>
          {comms === null && <div className="text-xs text-slate-400">Loading…</div>}
          {comms !== null && comms.length === 0 && (
            <div className="text-xs text-slate-400" data-testid="oem-view-no-comms">
              No communications yet — use the teal + icon in Actions to add one.
            </div>
          )}
          {(comms || []).map((m, i) => (
            <div key={i} className="border border-slate-200 px-3 py-2 mb-1.5 text-[11px]" data-testid={`oem-commlog-${i}`}>
              <div className="flex justify-between gap-2">
                <span className="font-bold text-teal-700 text-[10px] uppercase tracking-wider">
                  {m.date ? new Date(`${m.date}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </span>
                <span className="text-slate-400">{m.created_by || ""}</span>
              </div>
              <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{m.remark}</div>
            </div>
          ))}
          </>)}
        </div>
      </div>
    </div>
  );
}

function MField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
